import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { Container, NextButtonContainer } from './styled'
import {
  SummaryHeaderContainer,
  CompetenciesSubtitle,
  HorizontalDivider,
  CompetencyCard,
  CompetencyHeader,
  CompetencyNumber,
  CompetencyTitle,
  CompetencyDescription,
  QuestionsList,
  QuestionItem,
  QuestionText,
  QuestionNumber,
  ActionButtonsContainer,
  EditButton,
  DeleteButton,
  IconWrapper,
} from './skillsSummaryStyled'
import { H3, P } from 'views/components/UI/Text'

import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { Button, BackButton, StrokedButton } from 'views/components/UI/Buttons'
import { useNotification } from 'lib/context/notification.context'

import DeleteIcon from 'assets/icons/DeleteIcon'
import EditIcon from 'assets/icons/EditCoin'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ModalDelete from './modal-delete'
import { set360Data, setShowSummary as setReduxShowSummary } from 'ducks/test360'
import { platformAdminRoutes } from 'router/routes'
import test360UpdateDraft from 'services/process/test360/test360UpdateDraft'
import { stepThreeSelector } from 'ducks/createProcess'

export interface Filters {
  levelId: number
  groupId?: number
  name?: string
  page: number
}

interface ThreeDSkillsProps {
  id: number
  name: string
}

export interface ModelSkillsProps {
  id: number
  name: string
  skills3d: ThreeDSkillsProps[]
}

export interface SkillProps360 {
  name: string
  description: string
  questions: string[]
}

interface Props {
  skills360: SkillProps360[]
  setSkills360: (skills: SkillProps360[]) => void
  setShowSummary: (show: boolean) => void
}

const SkillsSummary: React.FC<Props> = ({ skills360, setSkills360, setShowSummary }) => {
  const navigate = useNavigate()

  const dispatch = useDispatch()
  const { getWarning, getError, getSuccess } = useNotification()
  const stepThreeData = useSelector(stepThreeSelector)

  const [skillIndexToDelete, setSkillIndexToDelete] = useState<number | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const handleBackButton = () => {
    setShowSummary(false)
    dispatch(setReduxShowSummary(false))
  }

  const handleAddSkill = () => {
    if (skills360.length < 10) {
      setShowSummary(false)
      dispatch(setReduxShowSummary(false))
    } else {
      getWarning('No puedes agregar más de 10 competencias')
    }
  }

  const handleRemoveSkill = (index: number) => {
    setSkillIndexToDelete(index)
    setShowDeleteModal(true)
  }

  const handleConfirmDeleteSkill = async () => {
    setLoading(true)
    try {
      if (skillIndexToDelete !== null) {
        const updatedSkills = [...skills360]
        updatedSkills.splice(skillIndexToDelete, 1)
        const body = {
          test360: {
            skills360: updatedSkills,
            step: 1,
          },
        }
        const updateResponse = await test360UpdateDraft(stepThreeData.processId, body)

        // Manejar error 402 de créditos insuficientes
        if (updateResponse && updateResponse.success === false && updateResponse.message) {
          getError(updateResponse.message)
          setLoading(false)
          return
        }

        setSkills360(updatedSkills)
        setSkillIndexToDelete(null)
        dispatch(set360Data(updatedSkills))
        setShowDeleteModal(false)
        setLoading(false)
        getSuccess('Competencia eliminada correctamente')
        if (updatedSkills.length === 0) {
          setShowSummary(false)
          dispatch(setReduxShowSummary(false))
        }
      } else {
        getError('No se pudo eliminar la competencia. Por favor, inténtalo de nuevo.')
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
      console.error('Error al eliminar la competencia:', error)
      getError('Ocurrió un error al eliminar la competencia. Por favor, inténtalo de nuevo.')
    }
  }
  const handleCancelDeleteSkill = () => {
    setSkillIndexToDelete(null)
    setShowDeleteModal(false)
  }

  const navigateToAddUsers = () => {
    if (skills360.length > 0) {
      setLoading(true)
      dispatch(set360Data(skills360))
      setTimeout(() => {
        setLoading(false)
        navigate(platformAdminRoutes.CreateUsers)
      }, 500)
    } else {
      getWarning('Debes agregar al menos una competencia antes de continuar')
    }
  }

  // MARK: RENDER

  return (
    <Container>
      {/* Header con título y botón de nueva competencia */}
      <SummaryHeaderContainer>
        <H3 margin="0">Editar y confirmar las competencias</H3>
        <StrokedButton
          onClick={handleAddSkill}
          type="button"
          style={{
            width: 'fit-content',
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
            margin: '0',
          }}
        >
          Nueva competencia
        </StrokedButton>
      </SummaryHeaderContainer>

      {/* Subtítulo con flecha */}
      <CompetenciesSubtitle>
        <P style={{ fontWeight: '600', fontSize: '18px', margin: 0, color: '#1F2124' }}>Competencias a evaluar</P>
        <ArrowDownwardIcon style={{ marginLeft: '5px', color: '#2D749C' }} />
      </CompetenciesSubtitle>

      {/* Línea horizontal */}
      <HorizontalDivider />

      {/* Lista de competencias */}
      {skills360.map((skill, index) => (
        <CompetencyCard key={`summary-skill-${index}`}>
          {/* Título de competencia con número */}
          <CompetencyHeader>
            <CompetencyNumber>{(index + 1).toString().padStart(2, '0')}.</CompetencyNumber>
            <CompetencyTitle>{skill.name}:</CompetencyTitle>
          </CompetencyHeader>

          {/* Descripción de la competencia */}
          <CompetencyDescription>{skill.description}</CompetencyDescription>

          {/* Lista de preguntas */}
          <QuestionsList>
            {skill.questions.map((question, qIndex) => (
              <QuestionItem key={`question-${index}-${qIndex}`}>
                <QuestionText>
                  <QuestionNumber>{`${index + 1}.${qIndex + 1}`}</QuestionNumber>
                  {question}
                </QuestionText>
              </QuestionItem>
            ))}
          </QuestionsList>

          {/* Botones de acción */}
          <ActionButtonsContainer>
            <EditButton onClick={handleBackButton}>
              <IconWrapper>
                <EditIcon />
              </IconWrapper>
              Editar
            </EditButton>
            <DeleteButton onClick={() => handleRemoveSkill(index)}>
              <IconWrapper>
                <DeleteIcon />
              </IconWrapper>
              Eliminar
            </DeleteButton>
          </ActionButtonsContainer>
        </CompetencyCard>
      ))}

      {/* Botones de navegación */}
      <NextButtonContainer>
        <BackButton onClick={handleBackButton} />
        <Button
          onClick={navigateToAddUsers}
          type="button"
          style={{
            marginLeft: 'auto',
          }}
        >
          Agregar usuarios
        </Button>
      </NextButtonContainer>

      <ModalLoader isLoading={loading} />
      <ModalDelete
        showModal={showDeleteModal}
        handleClose={handleCancelDeleteSkill}
        handleRemoveSkill={handleConfirmDeleteSkill}
      />
      {/* <ModalCleanup showModal={isModalOpen} handleClose={handleCloseConfirmModal} handleCleanup={handleConfirm} /> */}
    </Container>
  )
}

export default SkillsSummary
